import React, { useEffect, useState, useMemo } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { Spin } from "antd";
import axiosClient from "../../api/axios";
import LinkUpload from "../../api/LinkUpload";

// Import CSS và PageHeader
import './News.css';
import PageHeader from '../PageHeader/PageHeader'; // Đảm bảo đường dẫn này đúng

// =================== COMPONENT PHÂN TRANG ===================
const Pagination = ({ totalItems, itemsPerPage, currentPage, onPageChange }) => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    if (totalPages <= 1) return null;
    const handlePageClick = (page) => {
        if (page >= 1 && page <= totalPages) {
            onPageChange(page);
            window.scrollTo(0, 200); // Cuộn lên gần đầu nội dung
        }
    };
    const renderPageNumbers = () => {
        const pageNumbers = [];
        const startPage = Math.max(1, currentPage - 1);
        const endPage = Math.min(totalPages, currentPage + 1);
        if (startPage > 1) {
            pageNumbers.push(<li key={1}><a className="page-numbers" onClick={() => handlePageClick(1)}>1</a></li>);
            if (startPage > 2) pageNumbers.push(<li key="dots1"><span className="page-numbers dots">…</span></li>);
        }
        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(<li key={i}>{i === currentPage ? (<span className="page-numbers current">{i}</span>) : (<a className="page-numbers" onClick={() => handlePageClick(i)}>{i}</a>)}</li>);
        }
        if (endPage < totalPages) {
            if (endPage < totalPages - 1) pageNumbers.push(<li key="dots2"><span className="page-numbers dots">…</span></li>);
            pageNumbers.push(<li key={totalPages}><a className="page-numbers" onClick={() => handlePageClick(totalPages)}>{totalPages}</a></li>);
        }
        return pageNumbers;
    };
    return (
        <nav className="pagination">
            <ul>
                {currentPage > 1 && (<li><a className="prev page-numbers" onClick={() => handlePageClick(currentPage - 1)}>«</a></li>)}
                {renderPageNumbers()}
                {currentPage < totalPages && (<li><a className="next page-numbers" onClick={() => handlePageClick(currentPage + 1)}>»</a></li>)}
            </ul>
        </nav>
    );
};

// =================== COMPONENT POSTCARD ===================
const PostCard = ({ article, type = 'grid' }) => {
    const history = useHistory();
    if (!article) return null;

    const handleCardClick = (e) => {
        e.preventDefault();
        history.push(`/view/${article.Slug}`);
    };

    return (
        <a
            href={`/view/${article.Slug}`}
            onClick={handleCardClick}

        >
            <article className={`post-card post-card--${type}`}>
                <a href={`/view/${article.Slug}`} onClick={handleCardClick} className="post-card__thumb">
                    {/* === THAY ĐỔI LOGIC Ở ĐÂY === */}
                    {article.ImageUrl ? (
                        <img
                            src={LinkUpload(article.ImageUrl)}
                            alt={article.Title}
                        />
                    ) : (
                        <div className="placeholder-thumbnail">
                            <i className="fa-solid fa-image"></i>
                        </div>
                    )}
                    {/* ============================= */}
                </a>
                <div className="post-card__content">
                    <h3 className="post-card__title">
                        <a href={`/view/${article.Slug}`} onClick={handleCardClick}>
                            {article.Title}
                        </a>
                    </h3>
                    {type !== 'list' && (
                        <p className="post-card__summary">{article.Sumary}</p>
                    )}
                    <div className="post-card__meta">
                        <i className="fa-regular fa-clock"></i>
                        <span>{moment(article.PublishDate).format('DD/MM/YYYY')}</span>
                    </div>
                </div>
            </article>
        </a>
    );
};


// =================== COMPONENT CHÍNH CỦA TRANG NEWS ===================
const News = () => {
    const token = localStorage.getItem("token");
    const header = { Authorization: `Bearer ${token}` };

    const [allArticles, setAllArticles] = useState([]);
    const [allCategories, setAllCategories] = useState([]);
    const [loading, setLoading] = useState(true);

    // State cho phân trang
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 8; // Số lượng bài viết hiển thị trên mỗi trang

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const [articlesRes, categoriesRes] = await Promise.all([
                    axiosClient.post("/Articles/GetArticlesAll", "", { headers: header }),
                    axiosClient.post("/Categories/GetCategoriesAll", "", { headers: header }),
                ]);
                if (articlesRes.status === 200) {
                    setAllArticles(articlesRes.data.filter(a => a.Status === 1));
                }
                if (categoriesRes.status === 200) {
                    setAllCategories(categoriesRes.data || []);
                }
            } catch (error) {
                console.error("Lỗi khi tải dữ liệu trang tin tức:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // Logic gộp và sắp xếp tất cả bài viết từ các chuyên mục được chỉ định
    const combinedArticles = useMemo(() => {
        const categorySlugs = ["tintuc-sukien", "thongbao", "thu-vien-tai-lieu"];

        const categoryIds = allCategories
            .filter(cat => categorySlugs.includes(String(cat.Slug)))
            .map(cat => cat.ID);

        return allArticles
            .filter(article => categoryIds.includes(article.IDCategory))
            .sort((a, b) => new Date(b.PublishDate) - new Date(a.PublishDate));
    }, [allArticles, allCategories]);

    // Logic phân trang: Lấy ra các bài viết cho trang hiện tại
    const currentArticlesOnPage = useMemo(() => {
        const firstItemIndex = (currentPage - 1) * itemsPerPage;
        const lastItemIndex = firstItemIndex + itemsPerPage;
        return combinedArticles.slice(firstItemIndex, lastItemIndex);
    }, [currentPage, combinedArticles]);

    // Dữ liệu tĩnh cho PageHeader
    const breadcrumbsData = [
        { ID: 'home', Title: 'Trang chủ', Url: '/' },
        { ID: 'news', Title: 'Tin mới nhất', Url: '/tin-moi-nhat' } // Cập nhật URL nếu cần
    ];

    if (loading) {
        return <div style={{ textAlign: 'center', padding: '100px' }}><Spin size="large" /></div>;
    }

    return (
        <>
            <PageHeader
                breadcrumbs={breadcrumbsData}
                pageTitle="Tin mới nhất"
            />

            <div className="container main-body">
                <main className="content-col">
                    <section className="content-section">
                        <div className="category-grid-wrapper">
                            {currentArticlesOnPage.length > 0 ? (
                                currentArticlesOnPage.map(article => (
                                    <PostCard key={article.ID} article={article} type="grid" />
                                ))
                            ) : (
                                <p>Không có bài viết nào để hiển thị.</p>
                            )}
                        </div>
                    </section>

                    <Pagination
                        totalItems={combinedArticles.length}
                        itemsPerPage={itemsPerPage}
                        currentPage={currentPage}
                        onPageChange={setCurrentPage}
                    />
                </main>
            </div>
        </>
    );
};

export default News;